import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";

const TOGGLE_SAVED_LISTING_MUTATION = gql`
  mutation ToggleSavedListing($listingId: ID!) {
    toggleSavedListing(listingId: $listingId) {
      saved
    }
  }
`;

interface Props {
  listingId: string;
  initialSaved: boolean;
}

export function SaveListingButton({ listingId, initialSaved }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(initialSaved);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      graphqlClient.request<{ toggleSavedListing: { saved: boolean } }>(
        TOGGLE_SAVED_LISTING_MUTATION,
        { listingId }
      ),
    onMutate: () => setSaved((v) => !v),
    onSuccess: (data) => {
      setSaved(data.toggleSavedListing.saved);
      queryClient.invalidateQueries({ queryKey: ["listing", listingId] });
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
    },
    onError: () => setSaved((v) => !v),
  });

  const handleClick = () => {
    if (!user) {
      navigate(`/login?redirect_to=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    mutate();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
        saved
          ? "bg-rose-50 text-rose-700 hover:bg-rose-100"
          : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
      }`}
    >
      <svg className="h-4 w-4" fill={saved ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
      </svg>
      {saved ? "Įsiminta" : "Įsiminti skelbimą"}
    </button>
  );
}
